import {
  DEFAULT_INTERNATIONAL_SPECIAL_OPS_SHIPPING_MESSAGE_WHEN_UNAVAILABLE,
  ShippingRule,
  shippingRulesByCountry,
  specialOpsShippingRulesByCountry,
} from "@utils/inventoryRules"
import {
  pickDomainCountry,
  resolveDomainCountry,
} from "@utils/resolveDomainCountry"

type InventoryData = Record<
  string,
  { INVENTORY_ITEM_TRACKED?: boolean } & Record<string, InventoryNode>
>
type InventoryNode = { isAvailable: boolean; quantity: number }
type Mode = "PDP" | "SOPDP"
type Options = {
  domainCountryCode?: string // optional explicit override (e.g., for multi-country host)
  hostname?: string // raw host string (e.g., window.location.hostname)
  mode?: Mode
  variantAvailability?:
    | "Sell Everywhere"
    | "Sell in INTL"
    | "Sell in US"
    | undefined
}

type Result = {
  isAvailable: boolean
  quantity: number
  shippingMessage?: string
  warehouseLocation?: string
}

const NOT_AVAILABLE: Result = { isAvailable: false, quantity: 0 }

export default function getAvailabilityByRegion(
  countryCode: string | undefined,
  sku: string,
  inventoryData: InventoryData,
  options?: Options
): Result {
  const inventoryForSku = inventoryData?.[sku]
  if (!inventoryForSku) return NOT_AVAILABLE

  // Back-compat
  if (!options?.mode) {
    const inventoryNodes: Array<ShippingRule> =
      shippingRulesByCountry[countryCode ?? "US"]
    const nodeWithAvailability: ShippingRule | undefined = inventoryNodes?.find(
      ({ location }) => {
        const warehouse = inventoryForSku[location]
        return warehouse && warehouse.isAvailable
      }
    )
    if (!nodeWithAvailability) return NOT_AVAILABLE
    const quantity = inventoryForSku[nodeWithAvailability.location].quantity
    return { isAvailable: quantity > 0, quantity }
  }

  const mode = options.mode
  const cc = (countryCode || "").toUpperCase()

  const resolved = resolveDomainCountry(options.hostname)
  const domainCC = pickDomainCountry(resolved, options.domainCountryCode)
  const isLocalized = resolved.isLocalized
  const isPrimary = resolved.isPrimary

  const rulesMap =
    mode === "SOPDP" ? specialOpsShippingRulesByCountry : shippingRulesByCountry

  let rules: ShippingRule[] | undefined
  if (mode === "PDP") {
    // PDP:
    // - Primary: use user CC, fallback to US
    // - Localized: force domain CC (no fallback)
    if (isLocalized && domainCC) {
      rules = rulesMap[domainCC]
    } else {
      rules = rulesMap[cc] || rulesMap["US"]
    }
  } else {
    // SOPDP:
    // - Primary: use user CC, fallback to UNKNOWN_LOCATION
    // - Localized: force domain CC (no fallback)
    if (isLocalized && domainCC) {
      rules = rulesMap[domainCC]
    } else {
      const k = cc || "UNKNOWN_LOCATION"
      rules = rulesMap[k] || rulesMap["UNKNOWN_LOCATION"]
    }
  }

  // SOPDP gating per variant availability + domain
  if (mode === "SOPDP") {
    const variantAvailability = options?.variantAvailability
    const userIsUS = cc === "US"

    if (variantAvailability === "Sell in US") {
      // - Primary + non-US user => unavailable
      // - Localized => unavailable
      if ((isPrimary && !userIsUS) || isLocalized) {
        return {
          isAvailable: false,
          quantity: 0,
          shippingMessage:
            DEFAULT_INTERNATIONAL_SPECIAL_OPS_SHIPPING_MESSAGE_WHEN_UNAVAILABLE,
        }
      }
    }
  }

  const picked = firstStockedWarehouse(rules, inventoryForSku)
  if (!picked) return NOT_AVAILABLE

  return {
    isAvailable: true,
    quantity: picked.quantity,
    shippingMessage: picked.shippingMessage,
    warehouseLocation: picked.location,
  }
}

function firstStockedWarehouse(
  rules: ShippingRule[] | undefined,
  inventoryForSku: Record<string, InventoryNode> | undefined
) {
  if (!rules || !rules.length || !inventoryForSku) return undefined
  for (const { location, shippingMessage } of rules) {
    const q = Number(inventoryForSku[location]?.quantity ?? 0)
    if (q > 0) return { location, quantity: q, shippingMessage }
  }
  return undefined
}
